/**
 * listExpand.js — one implementation of "show the first N rows, with a way to see the rest".
 *
 * Every list view in the app grew its own answer to this, or none at all: leads capped at
 * 100 behind a bespoke "Show N more" button, most others rendered the entire collection,
 * and a few silently sliced to a fixed count with no way to reach what was hidden. So the
 * same report ("this list is enormous" / "I can't see the rest") arrived once per page,
 * and each fix landed in one route's private ensureStyles().
 *
 * This is deliberately NOT a list component. The routes render wildly different markup —
 * tables, cards, rows, pending-invite strips — and rewriting them all around one renderer
 * would be a far bigger change than the problem justifies. It is the smallest shared
 * thing that removes the duplication: decide how many rows to show, and render the
 * control that reveals the rest.
 *
 * WHY A CAP AND NOT PAGINATION. The cap is about the page being readable and cheap to
 * render, not about how much data was fetched. Server-side paging (see the leads list's
 * `limit`/`total`) is a separate concern and stays where it is; this only governs how many
 * of the rows a route ALREADY HAS get put into the DOM at once.
 *
 * NOT FOR: anything already virtualised, or a list whose whole point is completeness in
 * one view (an export preview, a print sheet).
 */

/**
 * Rows shown before the expand control appears — the app-wide standard.
 *
 * One number, everywhere, with no page-specific exceptions: the moment lists are allowed
 * their own caps, "how much of this list do I see" stops being a thing you learn once and
 * becomes a thing you rediscover per page. Raise it here and every list moves together.
 */
export const LIST_CAP = 25;

/**
 * Sizes offered by the control. 'ALL' is a real option, not a courtesy: a reader who
 * wants to Ctrl-F the whole list, or print it, is otherwise stuck clicking.
 *
 * LIST_CAP is the default and stays the first entry, so "how much of this list do I see
 * by default" is still one number app-wide.
 */
export const PAGE_SIZES = [LIST_CAP, 50, 100, 'ALL'];

/**
 * The size a reader last picked, per list.
 *
 * Keyed per list rather than shared app-wide, deliberately. One global number would mean
 * choosing "All" on a twenty-row settings list silently renders a thousand-row leads
 * table on the next visit — a performance footgun created by an unrelated click. Session
 * only (a plain Map, like routeHandoff.js): it does not survive a reload, which is the
 * same graceful degradation and avoids owning a persistence/expiry story.
 */
const sizeByList = new Map();

/** Test seam: forget remembered sizes. */
export function _resetListSizes() { sizeByList.clear(); }

const sizeToRows = (size, total) => (size === 'ALL' ? total : Number(size) || LIST_CAP);

/**
 * How many rows to render right now.
 * @param {number} total - rows the route holds
 * @param {number} [shown] - rows currently revealed; defaults to the cap
 * @returns {number}
 */
export function visibleCount(total, shown = LIST_CAP) {
  const n = Number.isFinite(shown) && shown > 0 ? shown : LIST_CAP;
  return Math.min(n, Math.max(0, total));
}

/**
 * The page-size control, or '' when the whole list already fits in the smallest size.
 *
 * REPLACED "Show N more". That button could only ever go one direction, a page at a time:
 * reaching row 300 of a 1,000-row list took eleven clicks and there was no way back to a
 * short list afterwards. A size choice is the standard answer, and it is reversible.
 *
 * Shape matters here more than usual. Six of the containers this lands in are
 * `overflow-x: auto` table wrappers, and one is the 360px-wide notification panel, so the
 * control stays compact and never wider than its host — a numbered-page strip has an
 * intrinsic minimum width and would have made those wrappers scroll sideways.
 *
 * The count stays alongside the selector because it is what tells a reader whether they
 * are three rows from the end or three hundred — the same reason the old button carried
 * its remaining count.
 *
 * @param {object} opts
 * @param {number} opts.total - rows the route holds
 * @param {number} opts.shown - rows currently revealed
 * @param {string} [opts.size] - the selected size ('ALL' or a number)
 * @param {string} [opts.id]
 * @param {string} [opts.noun]
 * @returns {string} HTML, or '' when there is nothing to choose
 */
export function pageSizeControlHTML({ total = 0, shown = 0, size = LIST_CAP, noun = '', id = '' } = {}) {
  const smallest = Number(PAGE_SIZES[0]) || LIST_CAP;
  if (total <= smallest) return '';
  const what = noun ? ` ${escapeAttr(noun)}` : '';
  const visible = Math.min(shown || smallest, total);
  const opts = PAGE_SIZES
    // Never offer a size that shows no more than the list already has — "100" on a
    // 40-row list is a no-op the reader has to try to discover.
    .filter((v) => v === 'ALL' || Number(v) < total)
    .map((v) => {
      const val = String(v);
      const label = v === 'ALL' ? 'All' : val;
      return `<option value="${val}"${String(size) === val ? ' selected' : ''}>${label}</option>`;
    }).join('');
  return `<div class="m5t-list-size"${id ? ` id="${escapeAttr(id)}"` : ''}>
      <span class="m5t-list-size__count">Showing ${visible.toLocaleString()} of ${total.toLocaleString()}${what}</span>
      <label class="m5t-list-size__pick">
        <span>Show</span>
        <select class="m5t-list-size__sel" data-action="list-size" aria-label="Rows to show${what}">${opts}</select>
      </label>
    </div>`;
}

/**
 * Wire the control inside `root`. Calls `onSize(nextShown)` with the new row count; the
 * route re-renders however it already does.
 *
 * Returns a cleanup function — routes that re-render into the same container would
 * otherwise stack listeners on every repaint.
 *
 * @param {ParentNode} root
 * @param {object} opts
 * @param {number} opts.total - rows the route holds, so 'All' can resolve to a number
 * @param {(nextShown: number, size: string) => void} opts.onSize
 * @returns {() => void} cleanup
 */
export function wirePageSize(root, { total = 0, onSize } = {}) {
  const sel = root?.querySelector?.('[data-action="list-size"]');
  if (!sel || typeof onSize !== 'function') return () => {};
  const handler = (e) => onSize(sizeToRows(e.target.value, total), e.target.value);
  sel.addEventListener('change', handler);
  return () => sel.removeEventListener('change', handler);
}

/**
 * Cap an ALREADY-RENDERED list and wire its expand control, without touching how the
 * route renders.
 *
 * The slice-and-re-render form above needs a `shown` in route state and a re-render
 * function to call. Most routes have neither: they build one HTML string and assign it.
 * Asking each of them to grow paging state is how a shared fix turns into twenty
 * bespoke ones, so this variant does the whole job from the DOM — hide the overflow,
 * append the control, reveal a step per click.
 *
 * Trade-off, stated plainly: every row is still in the DOM, so this bounds what a reader
 * has to scan, not what the browser parses. That is the actual complaint ("this list is
 * enormous"), and where payload size is the problem the answer is server-side paging,
 * which is a different fix in a different layer.
 *
 * Idempotent: re-running on the same container clears its previous control and state, so
 * routes that re-render on every filter change can call it unconditionally.
 *
 * @param {ParentNode} container - element holding the rows
 * @param {object} opts
 * @param {string} opts.rowSelector - selects the repeating rows within `container`
 * @param {number} [opts.cap]
 * @param {number} [opts.step]
 * @param {string} [opts.noun]
 * @param {ParentNode} [opts.controlHost] - where the button goes; defaults to `container`
 * @returns {number} how many rows were hidden (0 when the list fits)
 */
export function capList(container, { rowSelector, cap = LIST_CAP, noun = '', controlHost, key } = {}) {
  if (!container || !rowSelector) return 0;
  container.querySelectorAll(':scope > .m5t-list-size, .m5t-list-size').forEach((b) => b.remove());

  const rows = [...container.querySelectorAll(rowSelector)];
  rows.forEach((r) => r.removeAttribute('hidden'));
  if (rows.length <= cap) return 0;

  // A reader who chose 100 does not want to be dropped back to 25 the moment a filter
  // change re-renders the list, which routes do on every keystroke in some cases.
  const listKey = key || noun || rowSelector;
  let size = sizeByList.get(listKey) ?? cap;
  let shown = sizeToRows(size, rows.length);
  const host = controlHost || container;

  const paint = () => {
    shown = sizeToRows(size, rows.length);
    rows.forEach((r, i) => {
      if (i < shown) r.removeAttribute('hidden');
      else r.setAttribute('hidden', '');
    });
    host.querySelectorAll('.m5t-list-size').forEach((b) => b.remove());
    const html = pageSizeControlHTML({ total: rows.length, shown, size, noun });
    if (!html) return;
    host.insertAdjacentHTML('beforeend', html);
    wirePageSize(host, {
      total: rows.length,
      onSize: (_next, chosen) => {
        size = chosen;
        sizeByList.set(listKey, chosen);
        paint();
      },
    });
  };

  paint();
  return Math.max(0, rows.length - shown);
}

function escapeAttr(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}
